import type { UUID } from '@aline/common';
import { compareAsc, parseISO } from 'date-fns';
import { uniqBy } from 'lodash';
import { Apollo } from 'src/gql/generated';

import type { ReviewAssignment } from '../side-panel/task-review/task-review-helpers';
import {
  getAssignmentUser,
  getIsReviewerRow,
  nestSubtasks,
  TASK_STATUS_LABEL,
} from '../side-panel/task-review/task-review-helpers';

export const TASK_GROUP_BY = {
  Assignee: 'assignee',
  None: 'none',
  Status: 'status',
} as const;
export type TaskGroupBy = (typeof TASK_GROUP_BY)[keyof typeof TASK_GROUP_BY];

export const TASK_GROUP_BY_LABEL = {
  [TASK_GROUP_BY.Assignee]: 'Assignee',
  [TASK_GROUP_BY.None]: 'None',
  [TASK_GROUP_BY.Status]: 'Status',
} satisfies Record<TaskGroupBy, string>;

const ALL_KEY = 'all';
const UNASSIGNED_KEY = 'unassigned';
export const ALINE_AI_KEY = 'aline-ai';

const STATUS_GROUP_ORDER = [
  Apollo.TaskStatus.Started,
  Apollo.TaskStatus.ChangesRequested,
  Apollo.TaskStatus.InReview,
  Apollo.TaskStatus.NotStarted,
  Apollo.TaskStatus.Template,
  Apollo.TaskStatus.Completed,
  Apollo.TaskStatus.Rejected,
] as const;

interface GroupableAssignment extends Pick<ReviewAssignment, 'role'> {
  permission: ReviewAssignment['permission'] & {
    id: UUID;
    role?: { id: UUID; roleName: string } | null;
  };
}

export interface GroupableTask {
  assignments: readonly GroupableAssignment[];
  createdAt: string;
  id: UUID;
  isDelegatedToAi: boolean;
  parentTask: { id: UUID } | null;
  status: Apollo.TaskStatus;
}

export interface TaskGroup<TTask extends GroupableTask> {
  key: string;
  label: string;
  /**
   * Who a task added from this group's "+" is assigned to, so it lands in the
   * group. Null when a new task could not land here (a status only a review
   * reaches, Completed, or Aline AI).
   */
  newTaskAssignees: Apollo.TaskAssigneeInput[] | null;
  rows: { subtasks: TTask[]; task: TTask }[];
  /** A task added from this group's "+" is started right away (the In progress group). */
  startsNewTask: boolean;
}

type GroupOwner = Pick<TaskGroup<GroupableTask>, 'key' | 'label' | 'newTaskAssignees'>;

const ADDABLE_STATUSES: ReadonlySet<Apollo.TaskStatus> = new Set([
  Apollo.TaskStatus.NotStarted,
  Apollo.TaskStatus.Started,
  Apollo.TaskStatus.Template,
]);

const UNASSIGNED_OWNER: GroupOwner = {
  key: UNASSIGNED_KEY,
  label: 'Unassigned',
  newTaskAssignees: [],
};

/**
 * Bulk-created tasks (e.g. from launching a template) share a createdAt, and
 * the server's row order is not guaranteed, so tie-break on id to keep the
 * rendered order stable across refetches and re-renders.
 */
export function sortTasksByCreation<TTask extends { createdAt: string; id: string }>(
  tasks: readonly TTask[]
): TTask[] {
  return [...tasks].sort(
    (a, b) => compareAsc(parseISO(a.createdAt), parseISO(b.createdAt)) || a.id.localeCompare(b.id)
  );
}

/**
 * Buckets root tasks for the single-phase list. Subtasks always travel with
 * their parent. A task with two assignees shows under each of them, so the
 * assignee view answers "what is on Sam's plate" without hiding shared work.
 * Empty groups are skipped, except `openGroupKey`, so a composer opened in it
 * has somewhere to render. An empty list has no groups, so its empty state shows.
 */
export function groupPhaseTasks<TTask extends GroupableTask>(options: {
  groupBy: TaskGroupBy;
  isTemplate: boolean;
  openGroupKey: string | null;
  tasks: readonly TTask[];
}): TaskGroup<TTask>[] {
  const { groupBy, isTemplate, openGroupKey, tasks } = options;
  const rows = nestSubtasks(sortTasksByCreation(tasks));
  if (!rows.length) return [];

  switch (groupBy) {
    case TASK_GROUP_BY.None:
      return [{ key: ALL_KEY, label: '', newTaskAssignees: [], rows, startsNewTask: false }];
    case TASK_GROUP_BY.Status:
      return groupRowsByStatus({ isTemplate, openGroupKey, rows });
    case TASK_GROUP_BY.Assignee:
      return groupRowsByAssignee({ openGroupKey, rows });
  }
}

/**
 * The group a task from the header's "Add task" lands in: it is created
 * unassigned and not started, so the composer opens where the row will appear.
 */
export function getNewTaskGroupKey(options: { groupBy: TaskGroupBy; isTemplate: boolean }) {
  const { groupBy, isTemplate } = options;
  switch (groupBy) {
    case TASK_GROUP_BY.None:
      return ALL_KEY;
    case TASK_GROUP_BY.Assignee:
      return UNASSIGNED_KEY;
    case TASK_GROUP_BY.Status:
      return isTemplate ? Apollo.TaskStatus.Template : Apollo.TaskStatus.NotStarted;
  }
}

function groupRowsByStatus<TTask extends GroupableTask>(options: {
  isTemplate: boolean;
  openGroupKey: string | null;
  rows: TaskGroup<TTask>['rows'];
}): TaskGroup<TTask>[] {
  const { isTemplate, openGroupKey, rows } = options;
  return STATUS_GROUP_ORDER.filter(
    status => (status === Apollo.TaskStatus.Template) === isTemplate
  ).flatMap(status => {
    const matching = rows.filter(row => row.task.status === status);
    if (!matching.length && status !== openGroupKey) return [];
    return [
      {
        key: status,
        label: TASK_STATUS_LABEL[status],
        newTaskAssignees: ADDABLE_STATUSES.has(status) ? [] : null,
        rows: matching,
        startsNewTask: status === Apollo.TaskStatus.Started,
      },
    ];
  });
}

function groupRowsByAssignee<TTask extends GroupableTask>(options: {
  openGroupKey: string | null;
  rows: TaskGroup<TTask>['rows'];
}): TaskGroup<TTask>[] {
  const { openGroupKey, rows } = options;
  const placements = rows.flatMap(row => getOwners(row.task).map(owner => ({ owner, row })));
  const alwaysShown = openGroupKey === UNASSIGNED_KEY ? [UNASSIGNED_OWNER] : [];
  return uniqBy([...alwaysShown, ...placements.map(({ owner }) => owner)], 'key')
    .map(owner => ({
      ...owner,
      rows: placements.filter(placement => placement.owner.key === owner.key).map(({ row }) => row),
      startsNewTask: false,
    }))
    .sort(compareOwnerGroups);
}

function getOwners(task: GroupableTask): GroupOwner[] {
  const owners = task.assignments
    .filter(assignment => !getIsReviewerRow(assignment))
    .map(assignment => {
      const user = getAssignmentUser(assignment);
      const { role } = assignment.permission;
      return {
        key: assignment.permission.id,
        label: user?.name ?? role?.roleName ?? 'Unknown assignee',
        newTaskAssignees: getOwnerAssignees({ roleId: role?.id, userId: user?.id }),
      };
    });
  if (owners.length) return owners;
  if (task.isDelegatedToAi)
    return [{ key: ALINE_AI_KEY, label: 'Aline AI', newTaskAssignees: null }];
  return [UNASSIGNED_OWNER];
}

function getOwnerAssignees(owner: {
  roleId: UUID | undefined;
  userId: UUID | undefined;
}): Apollo.TaskAssigneeInput[] | null {
  const role = Apollo.TaskAssigneeRole.Assignee;
  if (owner.userId) return [{ assignedUserId: owner.userId, role }];
  if (owner.roleId) return [{ assignedRoleId: owner.roleId, role }];
  return null;
}

function compareOwnerGroups(a: { key: string; label: string }, b: { key: string; label: string }) {
  if (a.key === UNASSIGNED_KEY) return 1;
  if (b.key === UNASSIGNED_KEY) return -1;
  return a.label.localeCompare(b.label);
}
